import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaArrowLeft,
  FaArrowRight,
  FaBolt,
  FaBullseye,
  FaCalendarCheck,
  FaCalendarAlt,
  FaClipboardList,
  FaRegMoneyBillAlt,
  FaChartLine,
  FaBookOpen,
  FaBook,
  FaUserCircle,
  FaBell,
  FaCheckCircle,
  FaClock,
  FaDownload,
  FaExclamationTriangle,
  FaTimesCircle,
  FaGraduationCap,
  FaFileAlt,
  FaUsers,
  FaRegStickyNote,
  FaTimes,
  FaVideo,
  FaExternalLinkAlt,
  FaKeyboard,
} from "react-icons/fa";
import { useAppTheme } from "../context/AppThemeContext";
import { useStudentProfile } from "../context/StudentProfileContext";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import AdmissionForm from "./AdmissionForm";
import ProctoredAssessment from "./ProctoredAssessment";

const formatDateLabel = (value) => {
  if (!value || value === "N/A") return "N/A";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatCurrencyLabel = (value) => {
  const numericValue = Number(value ?? 0);

  if (Number.isNaN(numericValue)) return "INR 0";

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(numericValue);
};

const normalizeOnlineClassSession = (session = {}, index = 0) => {
  const scheduledAt = session.scheduledAt || session.startAt || new Date().toISOString();
  const durationMinutes = Number(session.durationMinutes || session.duration || 60);
  const scheduledEndAt = session.scheduledEndAt || session.endAt || new Date(
    new Date(scheduledAt).getTime() + durationMinutes * 60 * 1000
  ).toISOString();
  const rawStatus = String(session.status || "scheduled").toLowerCase();
  const status = rawStatus === "scheduled"
    && !Number.isNaN(new Date(scheduledEndAt).getTime())
    && new Date(scheduledEndAt).getTime() <= Date.now()
    ? "expired"
    : rawStatus;
  return {
    id: session.id || session.sessionId || `session-${index}`,
    title: session.title || session.name || "Online Class Session",
    classId: session.classId || "",
    className: session.className || "Class",
    roomId: session.roomCode || session.roomId || "",
    joinUrl: session.joinUrl || "",
    joinable: typeof session.joinable === "boolean" ? session.joinable : status === "live",
    section: session.section || "",
    startAt: scheduledAt,
    scheduledAt,
    scheduledEndAt,
    startedAt: session.startedAt || "",
    endedAt: session.endedAt || "",
    durationMinutes,
    status,
    attendees: Array.isArray(session.attendees) ? session.attendees : [],
  };
};

const StudentDashboard = () => {
  const { theme, isDarkMode, setTheme, toggleTheme } = useAppTheme();
  const navigate = useNavigate();
  const { roomCode: sharedRoomCode } = useParams();
  const [exportingKey, setExportingKey] = useState(null);
  const [activeView, setActiveView] = useState(null);
  const [showCustomize, setShowCustomize] = useState(false);
  const [activeSection, setActiveSection] = useState("attendance");
  const [attendanceTab, setAttendanceTab] = useState("summary");
  const [academicsTab, setAcademicsTab] = useState("schedule");
  const [accountTab, setAccountTab] = useState("profile");
  const [resourcesTab, setResourcesTab] = useState("leave");
  const [leaveForm, setLeaveForm] = useState({ from: "", to: "", reason: "" });
  const [queryForm, setQueryForm] = useState({ subject: "", message: "" });
  const [aiExamAnswers, setAiExamAnswers] = useState({});
  const [aiExamResult, setAiExamResult] = useState(null);
  const [aiExamLoading, setAiExamLoading] = useState(false);
  const [showIdPreview, setShowIdPreview] = useState(false);
  const [classmatesList, setClassmatesList] = useState([]);
  const [classmatesLoading, setClassmatesLoading] = useState(false);
  const [selectedClassmate, setSelectedClassmate] = useState(null);
  const [classmateProfile, setClassmateProfile] = useState(null);
  const [classmateProfileLoading, setClassmateProfileLoading] = useState(false);
  const [onlineClassSessions, setOnlineClassSessions] = useState([]);
  const [onlineClassLoading, setOnlineClassLoading] = useState(false);
  const [joiningSessionId, setJoiningSessionId] = useState("");
  const [joinByCode, setJoinByCode] = useState("");
  const [joinByCodeError, setJoinByCodeError] = useState("");
  const [joiningByCode, setJoiningByCode] = useState(false);
  const { studentProfile, studentProfileLoading, studentProfileError } = useStudentProfile();
  const rawUserName = localStorage.getItem("currentUserName") || localStorage.getItem("username") || "";
  const fallbackDisplayName = rawUserName
    ? rawUserName
        .split("@")[0]
        .replace(/[._-]+/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : "Student";
  const authEmail = localStorage.getItem("authEmail") || rawUserName || "";
  const apiStudentInfo = studentProfile?.studentInfo || {};
  const displayName = apiStudentInfo.name || fallbackDisplayName;
  const schoolName = studentProfile?.schoolName || "ClassNX Public School";
  const paymentStatusLabel = studentProfile?.paymentStatus || "PENDING";
  const nextPaymentLabel = formatDateLabel(studentProfile?.nextPaymentDate || "N/A");
  const totalPaidValue = Number(studentProfile?.totalPaid ?? 0);
  const totalPaidLabel = formatCurrencyLabel(totalPaidValue);
  const isViewing = Boolean(activeView);
  const viewLabels = {
    schedule: "Schedule",
    attendance: "Attendance",
    fees: "Fees",
    marks: "Marks and CGPA",
    profile: "Profile",
    admission_fill: "Fill Admission Form",
    admission_view: "View Admission Form",
    leave: "Apply for Leave",
    id_card: "School ID Card",
    ai_exam: "AI Practice Exam",
  };
  const defaultDashboardPrefs = {
    showQuickViews: true,
    showOverview: true,
    showSchedule: true,
    showAttendance: true,
    showAttendanceBreakdown: true,
    showOnlineClasses: true,
    showAssessments: true,
    showAssignments: true,
    showExams: true,
    showGrades: true,
    showNotices: true,
    showAiExam: true,
    showProfileCard: true,
    showFees: true,
    showMarks: true,
    showIdCard: true,
    showLeaveForm: true,
    showAskTeacher: true,
    showClassmates: true,
    showNotes: true,
    showLibrary: true,
    showQuickLinks: true,
  };
  const loadDashboardPrefs = () => {
    try {
      const stored = localStorage.getItem("studentDashboardPrefs");
      if (!stored) return defaultDashboardPrefs;
      const parsed = JSON.parse(stored);
      return { ...defaultDashboardPrefs, ...parsed };
    } catch {
      return defaultDashboardPrefs;
    }
  };
  const [prefs, setPrefs] = useState(loadDashboardPrefs);
  const [draftPrefs, setDraftPrefs] = useState(loadDashboardPrefs);

  const [draftTheme, setDraftTheme] = useState(theme);

  const persistDashboardPrefs = (nextPrefs) => {
    localStorage.setItem("studentDashboardPrefs", JSON.stringify(nextPrefs));
  };

  useEffect(() => {
    if (showCustomize) {
      setDraftPrefs(prefs);
      setDraftTheme(theme);
    }
  }, [showCustomize, prefs, theme]);

  useEffect(() => {
    if (!showIdPreview) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowIdPreview(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showIdPreview]);

  const togglePref = (key) => {
    setDraftPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const resetPrefs = () => {
    setDraftPrefs(defaultDashboardPrefs);
  };

  const attendancePreferenceOptions = [
    { key: "showAttendance", label: "Attendance Summary" },
    { key: "showAttendanceBreakdown", label: "Attendance Breakdown" },
    { key: "showOnlineClasses", label: "Online Classes" },
    { key: "showAssessments", label: "Assessments" },
  ];

  const preferenceOptions = [
    { key: "showQuickViews", label: "Quick Views" },
    { key: "showOverview", label: "Overview Cards" },
    { key: "showSchedule", label: "Schedule" },
    { key: "showAssignments", label: "Assignments" },
    { key: "showExams", label: "Upcoming Exams" },
    { key: "showAiExam", label: "AI Practice Exam" },
    { key: "showGrades", label: "Recent Grades" },
    { key: "showNotices", label: "Notices" },
    { key: "showProfileCard", label: "Profile Card" },
    { key: "showFees", label: "Fee Summary" },
    { key: "showMarks", label: "Marks and CGPA" },
    { key: "showIdCard", label: "School ID Card" },
    { key: "showLeaveForm", label: "Leave Section" },
    { key: "showAskTeacher", label: "Ask Teacher" },
    { key: "showClassmates", label: "Classmates" },
    { key: "showNotes", label: "Teacher Notes" },
    { key: "showLibrary", label: "Library" },
    { key: "showQuickLinks", label: "Quick Links" },
  ];

  const arePrefsEqual = (left, right) => {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) return false;
    return leftKeys.every((key) => left[key] === right[key]);
  };

  const prefsChanged = !arePrefsEqual(draftPrefs, prefs);
  const themeChanged = draftTheme !== theme;
  const canApply = prefsChanged || themeChanged;

  const applyCustomization = () => {
    setPrefs(draftPrefs);
    setTheme(draftTheme);
    persistDashboardPrefs(draftPrefs);
    toast.success("Personalize your dashboard changes applied.");
    setShowCustomize(false);
  };

  const isAdmissionView = activeView === "admission_fill" || activeView === "admission_view";
  const showSchedule = (!isViewing && prefs.showSchedule) || activeView === "schedule";
  const showAttendance = (!isViewing && prefs.showAttendance) || activeView === "attendance";
  const showAttendanceBreakdown = (!isViewing && prefs.showAttendanceBreakdown) || activeView === "attendance";
  const showOnlineClasses = (!isViewing && prefs.showOnlineClasses) || activeView === "online-classes";
  const showAssignments = !isViewing && prefs.showAssignments;
  const showExams = !isViewing && prefs.showExams;
  const showGrades = !isViewing && prefs.showGrades;
  const showNotices = !isViewing && prefs.showNotices;
  const showAiExam = (!isViewing && prefs.showAiExam) || activeView === "ai_exam";
  const showProfile = (!isViewing && prefs.showProfileCard) || activeView === "profile";
  const showFees = (!isViewing && prefs.showFees) || activeView === "fees";
  const showMarks = (!isViewing && prefs.showMarks) || activeView === "marks";
  const showIdCard = (!isViewing && prefs.showIdCard) || activeView === "id_card";
  const showLeave = (!isViewing && prefs.showLeaveForm) || activeView === "leave";
  const showAskTeacher = !isViewing && prefs.showAskTeacher;
  const showClassmates = !isViewing && prefs.showClassmates;
  const showNotes = !isViewing && prefs.showNotes;
  const showLibrary = !isViewing && prefs.showLibrary;
  const showQuickLinks = !isViewing && prefs.showQuickLinks;
  const isCenteredSectionLayout = !isViewing && (activeSection === "account" || activeSection === "resources");
  const showLeftColumn = isViewing
    ? activeView === "schedule" || activeView === "attendance" || isAdmissionView || activeView === "ai_exam"
    : isCenteredSectionLayout
      ? false
    : (
        prefs.showSchedule
        || prefs.showAttendance
        || prefs.showAssignments
        || prefs.showExams
        || prefs.showGrades
        || prefs.showNotices
        || prefs.showAiExam
      );
  const showRightColumn = isViewing
    ? activeView === "profile"
      || activeView === "fees"
      || activeView === "marks"
      || activeView === "attendance"
      || activeView === "leave"
      || activeView === "id_card"
    : (
        prefs.showProfileCard
        || prefs.showAttendanceBreakdown
        || prefs.showFees
        || prefs.showMarks
        || prefs.showIdCard
        || prefs.showLeaveForm
        || prefs.showAskTeacher
        || prefs.showClassmates
        || prefs.showNotes
        || prefs.showLibrary
        || prefs.showQuickLinks
      );
  const showMainGrid = isViewing
    ? ["schedule", "attendance", "profile", "fees", "marks", "admission_fill", "admission_view", "leave", "id_card", "ai_exam"].includes(activeView)
    : (showLeftColumn || showRightColumn);
  // LMS records stay empty until their tenant-scoped APIs return real data.
  const schedule = [];
  const assignments = [];
  const upcomingExams = [];

  const aiExamTemplate = { subject: "", topic: "", duration: "", difficulty: "", instructions: "", questions: [] };
  const recentGrades = [];
  const marksCard = [];
  const cgpaSummary = { cgpa: "N/A", percentile: "N/A", rank: "N/A" };

  const totalScore = marksCard.reduce((sum, item) => sum + item.score, 0);
  const totalMax = marksCard.reduce((sum, item) => sum + item.max, 0);

  const notices = [];

  const studentInfo = {
    name: displayName,
    class: apiStudentInfo.studentClass || "10th Standard",
    rollNo: apiStudentInfo.rollNo || "N/A",
    admissionDate: formatDateLabel(apiStudentInfo.admissionDate),
    email: authEmail || "Not available",
  };
  const idCardInfo = {
    schoolName,
    studentName: displayName,
    class: studentInfo.class,
    rollNo: studentInfo.rollNo,
    admissionNo: apiStudentInfo.admissionNo || apiStudentInfo.admissionNumber || "N/A",
    bloodGroup: apiStudentInfo.bloodGroup || "N/A",
    emergencyContact: apiStudentInfo.emergencyContact || apiStudentInfo.guardianPhone || "N/A",
    guardianName: apiStudentInfo.guardianName || apiStudentInfo.fatherName || "N/A",
    validThru: apiStudentInfo.idCardValidThru ? formatDateLabel(apiStudentInfo.idCardValidThru) : "N/A",
  };

  const feeSummary = {
    total: totalPaidLabel,
    paid: totalPaidLabel,
    due: paymentStatusLabel,
    nextDue: nextPaymentLabel,
  };

  const paidPercent = paymentStatusLabel === "PAID" ? 100 : 0;
  const attendanceBreakdown = [];
  const attendanceSummary = { totalDays: 0, present: 0, absent: 0, late: 0, percentage: "N/A" };
  const studentClassLabel = String(apiStudentInfo.studentClass || "").toLowerCase();
  const visibleOnlineClasses = onlineClassSessions
    .filter((session) => {
      if (!studentClassLabel) return true;
      return String(session.className || "").toLowerCase().includes(studentClassLabel);
    })
    .sort((left, right) => new Date(right.startAt).getTime() - new Date(left.startAt).getTime());
  const onlineClassCounts = visibleOnlineClasses.reduce(
    (acc, session) => {
      if (session.status === "live") acc.live += 1;
      else if (session.status === "ended") acc.ended += 1;
      else if (session.status === "expired") acc.expired += 1;
      else acc.scheduled += 1;
      return acc;
    },
    { live: 0, scheduled: 0, expired: 0, ended: 0 }
  );

  const studentInitials = displayName
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const nextClass = schedule[0];
  const quickActionCards = [
    {
      key: "profile",
      title: "My profile",
      description: "Open your complete student profile and account details.",
      icon: FaUserCircle,
      actionType: "route",
      route: "/profile",
      cta: "Open profile",
      tone: isDarkMode ? "bg-slate-900 text-slate-100 border-slate-700" : "bg-slate-950 text-white border-slate-900",
    },
    {
      key: "admission_form",
      title: "Apply admission",
      description: "Open the admission form directly and update details now.",
      icon: FaFileAlt,
      actionType: "route",
      route: "/admission-form",
      cta: "Open form",
      tone: isDarkMode ? "bg-indigo-950/50 text-indigo-100 border-indigo-900/60" : "bg-indigo-50 text-indigo-800 border-indigo-200",
    },
    {
      key: "attendance",
      title: "Attendance",
      description: "Check present, absent, and late records.",
      icon: FaCalendarCheck,
      actionType: "view",
      cta: "View attendance",
      tone: isDarkMode ? "bg-emerald-950/50 text-emerald-100 border-emerald-900/60" : "bg-emerald-50 text-emerald-800 border-emerald-200",
    },
    {
      key: "marks",
      title: "Marks and CGPA",
      description: "See academic scores, total marks, and CGPA.",
      icon: FaBookOpen,
      actionType: "view",
      cta: "Open results",
      tone: isDarkMode ? "bg-amber-950/50 text-amber-100 border-amber-900/60" : "bg-amber-50 text-amber-800 border-amber-200",
    },
  ];

  const attendanceLog = [];

  const attendanceBadge = (status) => {
    if (status === "Present") {
      return { icon: FaCheckCircle, className: "text-emerald-700 bg-emerald-50" };
    }
    if (status === "Absent") {
      return { icon: FaTimesCircle, className: "text-rose-700 bg-rose-50" };
    }
    return { icon: FaClock, className: "text-amber-700 bg-amber-50" };
  };

  const libraryLoans = [];

  // Fetch classmates from API when section is visible
  useEffect(() => {
    const showClassmatesFlag = prefs?.showClassmates;
    if (!showClassmatesFlag || activeSection !== "resources") return;
    if (!classmatesList.length) setClassmatesLoading(true);
    api
      .get(API_PATHS.student.classmates())
      .then((res) => {
        const data = res.data?.data ?? res.data ?? [];
        setClassmatesList(Array.isArray(data) ? data : []);
      })
      .catch(() => setClassmatesList([]))
      .finally(() => setClassmatesLoading(false));
  }, [activeSection, prefs?.showClassmates]);

  // Notify student when a class goes live
  const notifiedLiveRef = React.useRef(new Set());
  useEffect(() => {
    const check = () => {
      onlineClassSessions.forEach((session) => {
        if (session.status === "live" && !notifiedLiveRef.current.has(session.id)) {
          notifiedLiveRef.current.add(session.id);
          toast.info(
            <span>
              <strong>{session.title}</strong> has started!{" "}
              <button
                style={{ background: "none", border: "none", color: "#0ea5e9", cursor: "pointer", padding: 0, fontWeight: 700 }}
                onClick={() => {
                  setActiveSection("attendance");
                  setAttendanceTab("online-classes");
                }}
              >
                Join now
              </button>
            </span>,
            { autoClose: 10000, icon: "🎥" }
          );
        }
      });
    };
    check();
    const interval = setInterval(check, 15000);
    return () => clearInterval(interval);
  }, [onlineClassSessions]);

  useEffect(() => {
    let active = true;
    const stopLoading = () => { active = false; };

    const loadOnlineClasses = async () => {
      setOnlineClassLoading(true);
      try {
        const response = await api.get(API_PATHS.student.onlineClasses());
        const payload = response?.data?.data ?? response?.data ?? [];
        if (Array.isArray(payload) && payload.length) {
          if (active) setOnlineClassSessions(payload.map(normalizeOnlineClassSession));
          return;
        }
      } catch (error) {
        if (!active || error?.name === "AbortError") return;
        toast.error("Unable to load online classes from server.");
      } finally {
        if (active) setOnlineClassLoading(false);
      }

      if (active) setOnlineClassSessions([]);
    };

    loadOnlineClasses();
    window.addEventListener("auth:logout", stopLoading);
    return () => {
      active = false;
      window.removeEventListener("auth:logout", stopLoading);
    };
  }, []);

  useEffect(() => {
    const refreshExpiredStatuses = () => {
      setOnlineClassSessions((current) => current.map(normalizeOnlineClassSession));
    };
    const intervalId = window.setInterval(refreshExpiredStatuses, 60 * 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  const joinByClassCode = async () => {
    const code = joinByCode.trim();
    if (!code) {
      setJoinByCodeError("Please enter a class code.");
      return;
    }

    setJoiningByCode(true);
    const matched = onlineClassSessions.find(
      (s) => s.roomId.toLowerCase() === code.toLowerCase()
    );
    if (matched) {
      setJoinByCodeError("");
      setJoinByCode("");
      await joinOnlineClass(matched);
      setJoiningByCode(false);
      return;
    }

    try {
      const response = await api.post(API_PATHS.student.joinOnlineClass(code));

      const payload = response?.data?.data ?? response?.data ?? {};
      const joinedSession = normalizeOnlineClassSession(payload.room || payload.session || payload);

      if (!joinedSession?.roomId) {
        setJoinByCodeError("Invalid class code.");
        setJoiningByCode(false);
        return;
      }

      setOnlineClassSessions((previous) => {
        const filtered = previous.filter((item) => item.id !== joinedSession.id);
        return [joinedSession, ...filtered];
      });

      setJoinByCodeError("");
      setJoinByCode("");
      toast.success("Joined class successfully. Attendance marked.");
      navigate(`/classroom/room/${encodeURIComponent(joinedSession.roomId)}`);
    } catch (error) {
      setJoinByCodeError(error?.message || "Class code is invalid or class is not live.");
    } finally {
      setJoiningByCode(false);
    }
  };

  const joinOnlineClass = async (session) => {
    if (!session?.joinable) {
      toast.error("This class is not currently joinable.");
      return;
    }
    if (!session?.roomId) {
      toast.error("Classroom code is unavailable for this session.");
      return;
    }

    const attendeeKey = (authEmail || rawUserName || displayName || "student").toLowerCase();
    const attendeeRecord = {
      id: attendeeKey,
      name: displayName || "Student",
      email: authEmail || rawUserName || "",
      joinedAt: new Date().toISOString(),
    };

    const previousSessions = onlineClassSessions;
    setJoiningSessionId(session.id);
    setOnlineClassSessions((previous) =>
      previous.map((item) => {
        if (item.id !== session.id) return item;
        const existing = (item.attendees || []).filter((entry) => String(entry.id || "").toLowerCase() !== attendeeKey);
        return { ...item, attendees: [attendeeRecord, ...existing], status: item.status === "scheduled" ? "live" : item.status };
      })
    );

    try {
      // `id` is the room database UUID. The join endpoint is explicitly
      // keyed by the public classroom code (for example, RHQK2EMU).
      const response = await api.post(API_PATHS.student.joinOnlineClass(session.roomId));
      const payload = response?.data?.data ?? response?.data ?? {};
      const confirmedSession = normalizeOnlineClassSession(payload.room || payload.session || payload);
      setOnlineClassSessions((current) => current.map((item) => (
        item.id === session.id ? { ...item, ...confirmedSession, attendees: item.attendees } : item
      )));
    } catch (error) {
      // Joining a classroom is an authorization-sensitive server operation;
      // never grant local access when the server did not confirm it.
      setOnlineClassSessions(previousSessions);
      toast.error(error?.message || "The server could not confirm classroom access.");
      return;
    } finally {
      setJoiningSessionId("");
    }

    toast.success("Joined class successfully. Attendance marked.");
    navigate(`/classroom/room/${encodeURIComponent(session.roomId)}`);
  };

  const sharedJoinAttemptRef = React.useRef("");
  useEffect(() => {
    const roomCode = String(sharedRoomCode || "").trim();
    if (!roomCode || sharedJoinAttemptRef.current === roomCode) return;
    sharedJoinAttemptRef.current = roomCode;
    setActiveSection("attendance");
    setAttendanceTab("online-classes");
    setJoiningByCode(true);

    api.post(API_PATHS.student.joinOnlineClass(roomCode))
      .then((response) => {
        const payload = response?.data?.data ?? response?.data ?? {};
        const joinedSession = normalizeOnlineClassSession(payload.room || payload.session || payload);
        if (!joinedSession.roomId) throw new Error("The server did not return a classroom code.");
        setOnlineClassSessions((previous) => [
          joinedSession,
          ...previous.filter((item) => item.id !== joinedSession.id && item.roomId !== joinedSession.roomId),
        ]);
        toast.success("Joined class successfully. Attendance marked.");
        navigate(`/classroom/room/${encodeURIComponent(joinedSession.roomId)}`, { replace: true });
      })
      .catch((error) => setJoinByCodeError(error?.message || "This link is invalid, unauthorized, or the class is not live."))
      .finally(() => setJoiningByCode(false));
  }, [navigate, sharedRoomCode]);

  const teacherNotes = [];
  const leaveHistory = [];

  const handleView = (key) => {
    setActiveView(key);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDashboardSectionSelect = (section) => {
    if (section.actionType === "route") {
      navigate(section.route);
    } else if (section.actionType === "view") {
      handleView(section.view);
    } else {
      clearView();
      setActiveSection(section.section);
    }
  };

  const clearView = () => {
    setActiveView(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLeaveSubmit = (event) => {
    event.preventDefault();
    if (!leaveForm.from || !leaveForm.to || !leaveForm.reason) {
      toast.error("Please fill all leave fields.");
      return;
    }
    toast.info("Leave submission will be enabled when the school leave API is connected.");
  };

  const handleQuerySubmit = (event) => {
    event.preventDefault();
    if (!queryForm.subject || !queryForm.message) {
      toast.error("Please add a subject and message.");
      return;
    }
    toast.info("Teacher messaging will be enabled when its API is connected.");
  };

  const leaveStatusClass = (status) => {
    if (status === "Approved") return "bg-emerald-50 text-emerald-700";
    if (status === "Rejected") return "bg-rose-50 text-rose-700";
    return "bg-amber-50 text-amber-700";
  };

  const fakeExport = async (label, key) => {
    if (exportingKey) return;
    setExportingKey(key);
    toast.info(`${label} export will be enabled when its backend endpoint is connected.`);
    setExportingKey(null);
  };

  const exportButtonClass = (key) =>
    `inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 transition ${
      exportingKey ? "opacity-60" : ""
    } ${exportingKey === key ? "cursor-wait" : ""}`;

  const quickViewButtonClass = `group inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
    isDarkMode
      ? "border-slate-800 bg-slate-900/70 text-slate-100 hover:border-amber-300/40 hover:text-amber-200"
      : "border-slate-200 bg-white/90 text-slate-700 hover:border-amber-200 hover:text-amber-700"
  }`;
  const panelClass = isDarkMode
    ? "rounded-[28px] border border-slate-800 bg-slate-900/70 shadow-[0_24px_80px_rgba(15,23,42,0.35)] backdrop-blur"
    : "rounded-[28px] border border-slate-200/80 bg-white/90 shadow-[0_24px_80px_rgba(148,163,184,0.22)] backdrop-blur";
  const softPanelClass = isDarkMode
    ? "rounded-3xl border border-slate-800 bg-slate-900/55 backdrop-blur"
    : "rounded-3xl border border-white/70 bg-white/80 backdrop-blur";
  const headingClass = isDarkMode ? "text-slate-50" : "text-slate-900";
  const mutedClass = isDarkMode ? "text-slate-300" : "text-slate-600";
  const subtleClass = isDarkMode ? "text-slate-400" : "text-slate-500";
  const canShowAcademicsSection = prefs.showSchedule || prefs.showAssignments || prefs.showExams || prefs.showGrades || prefs.showNotices || prefs.showAiExam;
  const canShowAttendanceSection = prefs.showAttendance || prefs.showAttendanceBreakdown || prefs.showOnlineClasses || prefs.showAssessments;
  const canShowAccountSection = prefs.showProfileCard || prefs.showFees || prefs.showMarks || prefs.showIdCard;
  const canShowResourcesSection = prefs.showLeaveForm || prefs.showAskTeacher || prefs.showClassmates || prefs.showNotes || prefs.showLibrary || prefs.showQuickLinks;
  const dashboardSections = [
    {
      id: "attendance",
      label: "My Attendance",
      icon: FaCalendarCheck,
      enabled: canShowAttendanceSection,
      description: "Attendance section",
      actionType: "section",
      section: "attendance",
    },
    {
      id: "report-card",
      label: "My Report Card",
      icon: FaGraduationCap,
      enabled: prefs.showMarks,
      description: "Marks Card and CGPA",
      actionType: "section",
      section: "account",
    },
    {
      id: "profile",
      label: "My Profile",
      icon: FaUserCircle,
      enabled: canShowAccountSection,
      description: "Profile and account",
      actionType: "section",
      section: "account",
    },
    {
      id: "apply-admission",
      label: "Apply Admission",
      icon: FaFileAlt,
      enabled: true,
      description: "Admission form",
      actionType: "section",
      section: "admission",
    },
    {
      id: "notifications",
      label: "Notifications",
      icon: FaBell,
      enabled: canShowAcademicsSection,
      description: "Notices and alerts",
      actionType: "section",
      section: "academics",
    },
  ].filter((section) => section.enabled);

  useEffect(() => {
    const handleSidebarAction = (event) => {
      const action = event?.detail?.action;
      if (!action) return;

      if (action === "attendance") {
        clearView();
        setActiveSection("attendance");
        setAttendanceTab("summary");
        return;
      }

      if (action === "online-classes") {
        clearView();
        setActiveSection("attendance");
        setAttendanceTab("online-classes");
        return;
      }

      if (action === "assessments") {
        clearView();
        setActiveSection("attendance");
        setAttendanceTab("assessments");
        return;
      }

      if (action === "report-card") {
        clearView();
        setActiveSection("account");
        setAccountTab("marks");
        return;
      }

      if (action === "my-profile") {
        clearView();
        setActiveSection("account");
        setAccountTab("profile");
        return;
      }

      if (action === "notifications") {
        clearView();
        setActiveSection("academics");
        setAcademicsTab("notices");
      }
    };

    window.addEventListener("student-dashboard:select-action", handleSidebarAction);
    return () => window.removeEventListener("student-dashboard:select-action", handleSidebarAction);
  }, []);

  useEffect(() => {
    if (
      !isViewing
      && !dashboardSections.some(
        (section) => section.actionType === "section" && section.section === activeSection,
      )
    ) {
      const firstSection = dashboardSections.find((section) => section.actionType === "section")?.section || "attendance";
      setActiveSection(firstSection);
    }
  }, [activeSection, dashboardSections, isViewing]);

  useEffect(() => {
    if (isViewing) return;

    if (activeSection === "attendance") {
      const available = [];
      if (prefs.showAttendance) available.push("summary");
      if (prefs.showAttendanceBreakdown) available.push("breakdown");
      if (prefs.showOnlineClasses) available.push("online-classes");
      if (prefs.showAssessments) available.push("assessments");
      if (!available.includes(attendanceTab)) setAttendanceTab(available[0] || "summary");
    }

    if (activeSection === "academics") {
      const available = [];
      if (prefs.showSchedule) available.push("schedule");
      if (prefs.showAssignments) available.push("assignments");
      if (prefs.showExams) available.push("exams");
      if (prefs.showGrades) available.push("grades");
      if (prefs.showNotices) available.push("notices");
      if (!available.includes(academicsTab)) setAcademicsTab(available[0] || "schedule");
    }

    if (activeSection === "account") {
      const available = [];
      if (prefs.showProfileCard) available.push("profile");
      if (prefs.showFees) available.push("fees");
      if (prefs.showMarks) available.push("marks");
      if (prefs.showIdCard) available.push("id_card");
      if (!available.includes(accountTab)) setAccountTab(available[0] || "profile");
    }

    if (activeSection === "resources") {
      const available = [];
      if (prefs.showLeaveForm) available.push("leave");
      if (prefs.showAskTeacher) available.push("ask");
      if (prefs.showClassmates) available.push("classmates");
      if (prefs.showNotes) available.push("notes");
      if (prefs.showLibrary) available.push("library");
      if (prefs.showQuickLinks) available.push("links");
      if (!available.includes(resourcesTab)) setResourcesTab(available[0] || "leave");
    }
  }, [
    activeSection,
    isViewing,
    attendanceTab,
    academicsTab,
    accountTab,
    resourcesTab,
    prefs.showAttendance,
    prefs.showAttendanceBreakdown,
    prefs.showOnlineClasses,
    prefs.showAssessments,
    prefs.showSchedule,
    prefs.showAssignments,
    prefs.showExams,
    prefs.showGrades,
    prefs.showNotices,
    prefs.showProfileCard,
    prefs.showFees,
    prefs.showMarks,
    prefs.showIdCard,
    prefs.showLeaveForm,
    prefs.showAskTeacher,
    prefs.showClassmates,
    prefs.showNotes,
    prefs.showLibrary,
    prefs.showQuickLinks,
  ]);

  return (
    <div
      className={`min-h-screen ${
        isDarkMode
          ? "bg-[radial-gradient(circle_at_top,_rgba(34,197,94,0.16),_transparent_28%),linear-gradient(180deg,_#020617_0%,_#0f172a_52%,_#111827_100%)] text-slate-100"
          : "bg-[radial-gradient(circle_at_top,_rgba(251,191,36,0.22),_transparent_28%),linear-gradient(180deg,_#fffdf7_0%,_#f8fafc_46%,_#eef2ff_100%)] text-slate-800"
      }`}
    >
      {showIdPreview && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/65 px-3 py-4 backdrop-blur-sm sm:items-center sm:p-6"
          role="dialog"
          aria-modal="true"
          onMouseDown={() => setShowIdPreview(false)}
        >
          <div
            className="relative w-full max-w-4xl overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur sm:px-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Live School Identity</p>
                <h3 className="mt-1 text-lg font-semibold text-slate-900 sm:text-xl">Student ID Preview</h3>
              </div>
            <button
              type="button"
              onClick={() => setShowIdPreview(false)}
              aria-label="Close ID preview"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
            >
              <FaTimes className="text-sm" />
            </button>
            </div>
            <div className="max-h-[85vh] overflow-y-auto">
              <div className="px-4 pb-4 pt-4 sm:px-6">
                <p className="text-sm text-slate-500">Realtime details from the current student dashboard profile.</p>
              </div>
              <div className="grid gap-4 px-4 pb-4 sm:px-6 sm:pb-6 lg:grid-cols-2 lg:gap-6">
                <div className="overflow-hidden rounded-[24px] bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 p-[1px] shadow-xl">
                  <div className="relative h-full rounded-[23px] bg-[radial-gradient(circle_at_top_right,_rgba(96,165,250,0.35),_transparent_30%),linear-gradient(135deg,_#0f172a_0%,_#111827_52%,_#1e1b4b_100%)] p-5 text-white sm:p-6">
                  <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-cyan-400/20 blur-3xl" />
                  <div className="absolute -left-10 bottom-0 h-28 w-28 rounded-full bg-amber-300/20 blur-3xl" />
                  <div className="relative flex items-start justify-between gap-4">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-white/65">{idCardInfo.schoolName}</p>
                      <h4 className="mt-3 text-xl font-semibold sm:text-2xl">Student Identity Card</h4>
                    </div>
                    <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-200">
                      Active
                    </span>
                  </div>

                  <div className="relative mt-8 flex flex-col gap-5 sm:flex-row sm:items-center">
                    <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-[22px] border border-white/15 bg-white/10 text-2xl font-semibold backdrop-blur-sm sm:h-24 sm:w-24 sm:text-3xl">
                      {studentInitials || "SP"}
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.22em] text-white/55">Card Holder</p>
                      <p className="mt-2 text-xl font-semibold sm:text-2xl">{idCardInfo.studentName}</p>
                      <p className="mt-1 text-sm text-white/70">{idCardInfo.class} • Roll {idCardInfo.rollNo}</p>
                      <p className="mt-1 text-sm text-white/70">Admission No. {idCardInfo.admissionNo}</p>
                    </div>
                  </div>

                  <div className="relative mt-8 grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-2xl border border-white/12 bg-white/10 p-3 backdrop-blur-sm">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-white/55">Blood Group</p>
                      <p className="mt-2 font-semibold">{idCardInfo.bloodGroup}</p>
                    </div>
                    <div className="rounded-2xl border border-white/12 bg-white/10 p-3 backdrop-blur-sm">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-white/55">Valid Thru</p>
                      <p className="mt-2 font-semibold">{idCardInfo.validThru}</p>
                    </div>
                  </div>

                  <div className="relative mt-8 flex items-end justify-between gap-4 border-t border-white/10 pt-5">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-white/50">Support Contact</p>
                      <p className="mt-2 text-sm font-medium text-white/85">{idCardInfo.emergencyContact}</p>
                    </div>
                    <div className="grid grid-cols-4 gap-1 rounded-xl bg-white/10 p-2">
                      {Array.from({ length: 24 }).map((_, index) => (
                        <span
                          key={`qr-front-${index}`}
                          className={`h-2.5 w-2.5 rounded-[2px] ${index % 3 === 0 || index % 5 === 0 ? "bg-white/90" : "bg-white/20"}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                </div>

                <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 shadow-inner sm:p-5">
                  <div className="rounded-[22px] bg-white p-4 shadow-sm sm:p-5">
                  <div className="flex items-center justify-between gap-4 border-b border-slate-100 pb-4">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">Verification Details</p>
                      <h4 className="mt-2 text-lg font-semibold text-slate-900 sm:text-xl">Card Back</h4>
                    </div>
                    <div className="grid grid-cols-4 gap-1 rounded-xl border border-slate-200 bg-slate-50 p-2">
                      {Array.from({ length: 24 }).map((_, index) => (
                        <span
                          key={`qr-back-${index}`}
                          className={`h-2.5 w-2.5 rounded-[2px] ${index % 4 === 0 || index % 7 === 0 ? "bg-slate-800" : "bg-slate-300"}`}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">School</p>
                      <p className="mt-2 text-sm font-semibold text-slate-900">{idCardInfo.schoolName}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Guardian</p>
                      <p className="mt-2 text-sm font-semibold text-slate-900">{idCardInfo.guardianName}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Emergency Contact</p>
                      <p className="mt-2 text-sm font-semibold text-slate-900">{idCardInfo.emergencyContact}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Issued On</p>
                      <p className="mt-2 text-sm font-semibold text-slate-900">{studentInfo.admissionDate}</p>
                    </div>
                  </div>

                  <div className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
                    This card should be presented during campus entry, examinations, library access, and official verification.
                  </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="w-full">
        <section className="relative">

          <div className="space-y-6 px-3 pb-4 pt-0 md:px-6 md:pb-8 md:pt-0">
            {isViewing && (
              <div className={`${softPanelClass} mb-4 flex flex-wrap items-center justify-between gap-3 px-4 py-3 view-fade`}>
                <button
                  type="button"
                  onClick={clearView}
                  className={`inline-flex items-center gap-2 text-sm font-medium ${
                    isDarkMode ? "text-slate-300 hover:text-slate-100" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  <FaArrowLeft />
                  Back to Dashboard
                </button>
                <span className={`rounded-full border px-3 py-1 text-xs uppercase tracking-wide ${isDarkMode ? "border-indigo-900/80 bg-indigo-950/50 text-indigo-100" : "border-indigo-200 bg-indigo-50 text-indigo-600"}`}>
                  Viewing: {viewLabels[activeView] || "Section"}
                </span>
              </div>
            )}

            {!isViewing && activeSection === "attendance" && (showAttendance || showAttendanceBreakdown) && (
              <div className={`${softPanelClass} flex flex-wrap items-center gap-2 px-3 py-2`}>
                {showAttendance && (
                  <button
                    type="button"
                    onClick={() => setAttendanceTab("summary")}
                    className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${attendanceTab === "summary" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                  >
                    My Attendance
                  </button>
                )}
                {showAttendanceBreakdown && (
                  <button
                    type="button"
                    onClick={() => setAttendanceTab("breakdown")}
                    className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${attendanceTab === "breakdown" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                  >
                    Attendance Breakdown
                  </button>
                )}
                {showOnlineClasses && (
                  <button
                    type="button"
                    onClick={() => setAttendanceTab("online-classes")}
                    className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${attendanceTab === "online-classes" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                  >
                    Online Classes
                  </button>
                )}
                {prefs.showAssessments && (
                  <button
                    type="button"
                    onClick={() => setAttendanceTab("assessments")}
                    className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${attendanceTab === "assessments" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                  >
                    Assessments
                  </button>
                )}
              </div>
            )}

            {!isViewing && activeSection === "academics" && (
              <div className={`${softPanelClass} flex flex-wrap items-center gap-2 px-3 py-2`}>
                {showSchedule && <button type="button" onClick={() => setAcademicsTab("schedule")} className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${academicsTab === "schedule" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Schedule</button>}
                {showAssignments && <button type="button" onClick={() => setAcademicsTab("assignments")} className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${academicsTab === "assignments" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Assignments</button>}
                {showExams && <button type="button" onClick={() => setAcademicsTab("exams")} className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${academicsTab === "exams" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Exams</button>}
                {showGrades && <button type="button" onClick={() => setAcademicsTab("grades")} className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${academicsTab === "grades" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Grades</button>}
                {showNotices && <button type="button" onClick={() => setAcademicsTab("notices")} className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${academicsTab === "notices" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Notifications</button>}
              </div>
            )}

            {!isViewing && activeSection === "account" && (
              <div className={`${softPanelClass} flex flex-wrap items-center gap-2 px-3 py-2`}>
                {showProfile && <button type="button" onClick={() => setAccountTab("profile")} className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${accountTab === "profile" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>My Profile</button>}
                {showFees && <button type="button" onClick={() => setAccountTab("fees")} className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${accountTab === "fees" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Fees</button>}
                {showMarks && <button type="button" onClick={() => setAccountTab("marks")} className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${accountTab === "marks" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Marks & CGPA</button>}
                {showIdCard && <button type="button" onClick={() => setAccountTab("id_card")} className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${accountTab === "id_card" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>ID Card</button>}
              </div>
            )}

            {!isViewing && activeSection === "resources" && (
              <div className={`${softPanelClass} flex flex-wrap items-center gap-2 px-3 py-2`}>
                {showLeave && <button type="button" onClick={() => setResourcesTab("leave")} className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${resourcesTab === "leave" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Leave</button>}
                {showAskTeacher && <button type="button" onClick={() => setResourcesTab("ask")} className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${resourcesTab === "ask" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Ask Teacher</button>}
                {showClassmates && <button type="button" onClick={() => setResourcesTab("classmates")} className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${resourcesTab === "classmates" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Classmates</button>}
                {showNotes && <button type="button" onClick={() => setResourcesTab("notes")} className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${resourcesTab === "notes" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Notes</button>}
                {showLibrary && <button type="button" onClick={() => setResourcesTab("library")} className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${resourcesTab === "library" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Library</button>}
                {showQuickLinks && <button type="button" onClick={() => setResourcesTab("links")} className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${resourcesTab === "links" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Quick Links</button>}
              </div>
            )}

        {!isViewing && activeSection === "overview" && prefs.showQuickViews && (
          <section className={`${panelClass} p-5 sm:p-6`}>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className={`text-xs uppercase tracking-[0.28em] ${subtleClass}`}>Essentials</p>
                <h2 className={`mt-2 text-2xl font-semibold ${headingClass}`}>Open what you need quickly</h2>
                <p className={`mt-2 text-sm ${mutedClass}`}>Only the actions students use most, without the extra clutter.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowCustomize(true)}
                className={quickViewButtonClass}
              >
                <FaBullseye />
                Customize dashboard
              </button>
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {quickActionCards.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => {
                      if (item.actionType === "route") {
                        navigate(item.route);
                        return;
                      }
                      handleView(item.key);
                    }}
                    className={`group rounded-[24px] border p-4 text-left transition duration-200 hover:-translate-y-1 hover:shadow-xl sm:p-5 ${item.tone}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <span
                        className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl text-lg shadow-sm ${
                          isDarkMode ? "bg-slate-950/60" : "bg-white/70"
                        }`}
                      >
                        <Icon />
                      </span>
                      <FaArrowRight className="mt-1 opacity-60 transition group-hover:translate-x-1" />
                    </div>
                    <h3 className="mt-5 text-lg font-semibold">{item.title}</h3>
                    <p className="mt-2 text-sm leading-6 opacity-80">{item.description}</p>
                    <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold opacity-90">
                      <span>{item.cta}</span>
                      <FaArrowRight className="transition group-hover:translate-x-1" />
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-4 flex flex-wrap gap-3">
              <button type="button" onClick={() => navigate("/admission-form")} className={quickViewButtonClass}>
                <FaFileAlt />
                Admission form
              </button>
              <button type="button" onClick={() => navigate("/view-admission-form")} className={quickViewButtonClass}>
                <FaFileAlt />
                Submitted form
              </button>
              {prefs.showIdCard && (
                <button type="button" onClick={() => handleView("id_card")} className={quickViewButtonClass}>
                  <FaUserCircle />
                  ID card
                </button>
              )}
            </div>
          </section>
        )}

        {showMainGrid && (
        <section className={`mt-6 grid grid-cols-1 gap-6 ${isViewing || isCenteredSectionLayout ? "lg:grid-cols-1" : "lg:grid-cols-3"}`}>
          {showLeftColumn && (
            <div className="lg:col-span-2 space-y-6">
            {((isViewing && activeView === "schedule") || (!isViewing && activeSection === "academics" && academicsTab === "schedule")) && showSchedule && (
            <div id="schedule" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm view-fade">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <FaBookOpen className="text-indigo-600" />
                  <h2 className="text-lg font-semibold text-slate-900">This Week's Schedule</h2>
                </div>
                <button
                  type="button"
                  onClick={() => fakeExport("Schedule", "schedule")}
                  className={exportButtonClass("schedule")}
                  disabled={Boolean(exportingKey)}
                >
                  <FaDownload />
                  Export
                </button>
              </div>
              <div className="mt-4 space-y-3 md:hidden">
                {schedule.map((item) => (
                  <div key={`${item.day}-${item.subject}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">{item.day}</p>
                        <p className="mt-2 text-base font-semibold text-slate-900">{item.subject}</p>
                      </div>
                      <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600 shadow-sm">
                        {item.time}
                      </span>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                      <div className="rounded-lg bg-white px-3 py-2">
                        <p className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Room</p>
                        <p className="mt-1 font-medium text-slate-700">{item.room}</p>
                      </div>
                      <div className="rounded-lg bg-white px-3 py-2">
                        <p className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Teacher</p>
                        <p className="mt-1 font-medium text-slate-700">{item.teacher}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 hidden overflow-x-auto md:block">
                <table className="w-full text-left text-sm">
                  <thead className="text-slate-500">
                    <tr>
                      <th className="pb-2 font-medium">Day</th>
                      <th className="pb-2 font-medium">Time</th>
                      <th className="pb-2 font-medium">Subject</th>
                      <th className="pb-2 font-medium">Room</th>
                      <th className="pb-2 font-medium">Teacher</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.map((item) => (
                      <tr key={`${item.day}-${item.subject}`} className="border-t border-slate-100">
                        <td className="py-3 font-medium text-slate-700">{item.day}</td>
                        <td className="py-3 text-slate-600">{item.time}</td>
                        <td className="py-3 text-slate-700">{item.subject}</td>
                        <td className="py-3 text-slate-600">{item.room}</td>
                        <td className="py-3 text-slate-600">{item.teacher}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            )}

            {((isViewing && activeView === "attendance") || (!isViewing && activeSection === "attendance" && attendanceTab === "summary")) && showAttendance && (
            <div id="attendance" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm view-fade">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <FaCalendarCheck className="text-emerald-600" />
                  <h2 className="text-lg font-semibold text-slate-900">My Attendance</h2>
                </div>
                <button
                  type="button"
                  onClick={() => fakeExport("Attendance", "attendance")}
                  className={exportButtonClass("attendance")}
                  disabled={Boolean(exportingKey)}
                >
                  <FaDownload />
                  Export
                </button>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Total Days</p>
                  <p className="text-lg font-semibold text-slate-800">{attendanceSummary.totalDays}</p>
                </div>
                <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Attendance</p>
                  <p className="text-lg font-semibold text-emerald-700">{attendanceSummary.percentage}</p>
                </div>
                <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Present</p>
                  <p className="text-lg font-semibold text-emerald-700">{attendanceSummary.present}</p>
                </div>
                <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Absent</p>
                  <p className="text-lg font-semibold text-rose-700">{attendanceSummary.absent}</p>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                {attendanceLog.map((entry) => {
                  const badge = attendanceBadge(entry.status);
                  const Icon = badge.icon;
                  return (
                    <div key={entry.date} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{entry.date}</p>
                        <p className="text-xs text-slate-500">{entry.day}</p>
                      </div>
                      <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${badge.className}`}>
                        <Icon />
                        {entry.status}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            )}

            {(!isViewing && activeSection === "attendance" && attendanceTab === "online-classes" && showOnlineClasses) && (
              <div className="rounded-3xl border border-cyan-100 bg-[linear-gradient(170deg,#ffffff_0%,#ecfeff_52%,#f0f9ff_100%)] p-6 shadow-[0_24px_60px_rgba(8,145,178,0.16)] view-fade">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <FaVideo className="text-sky-600" />
                    <h2 className="text-lg font-semibold tracking-wide text-slate-900">Online Live Classes</h2>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs font-semibold">
                    <span className="rounded-full border border-emerald-200 bg-emerald-100 px-3 py-1 text-emerald-800">Live {onlineClassCounts.live}</span>
                    <span className="rounded-full border border-amber-200 bg-amber-100 px-3 py-1 text-amber-800">Scheduled {onlineClassCounts.scheduled}</span>
                    <span className="rounded-full border border-rose-200 bg-rose-100 px-3 py-1 text-rose-800">Expired {onlineClassCounts.expired}</span>
                    <span className="rounded-full border border-slate-300 bg-slate-100 px-3 py-1 text-slate-700">Ended {onlineClassCounts.ended}</span>
                  </div>
                </div>

                {/* Join by class code */}
                <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 p-4">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-sky-700">Join by class code</p>
                  <div className="flex flex-wrap gap-2">
                    <input
                      type="text"
                      value={joinByCode}
                      onChange={(e) => { setJoinByCode(e.target.value); setJoinByCodeError(""); }}
                      onKeyDown={(e) => e.key === "Enter" && joinByClassCode()}
                      placeholder="Enter class code e.g. ABCD2345"
                      className="flex-1 min-w-0 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                    />
                    <button
                      type="button"
                      onClick={joinByClassCode}
                      disabled={joiningByCode}
                      className="inline-flex items-center gap-1.5 rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 active:bg-sky-800"
                    >
                      <FaKeyboard />
                      {joiningByCode ? "Joining..." : "Join class"}
                    </button>
                  </div>
                  {joinByCodeError && <p className="mt-1.5 text-xs text-rose-600">{joinByCodeError}</p>}
                </div>

                <div className="mt-4 space-y-3">
                  {onlineClassLoading && (
                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 text-sm text-slate-500">
                      Loading online classes...
                    </div>
                  )}

                  {!onlineClassLoading && visibleOnlineClasses.length === 0 && (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-5 text-sm text-slate-500">
                      No online class sessions available right now.
                    </div>
                  )}

                  {!onlineClassLoading && visibleOnlineClasses.map((session) => {
                    const alreadyJoined = (session.attendees || []).some(
                      (entry) => String(entry.id || "").toLowerCase() === String(authEmail || rawUserName || displayName).toLowerCase()
                    );
                    const statusClass =
                      session.status === "live"
                        ? "bg-emerald-100 text-emerald-700"
                        : session.status === "ended"
                        ? "bg-slate-200 text-slate-700"
                        : session.status === "expired"
                        ? "bg-rose-100 text-rose-700"
                        : "bg-amber-100 text-amber-700";

                    return (
                      <div key={session.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold tracking-wide text-slate-900">{session.title}</p>
                            <p className="mt-1 text-xs text-slate-500">
                              {session.className} • {new Date(session.startAt).toLocaleString()} • {session.durationMinutes} min
                            </p>
                            <p className="mt-1 text-xs text-slate-500">Join deadline {new Date(session.scheduledEndAt).toLocaleString()}</p>
                            <p className="mt-1 text-xs text-slate-500">Class code {session.roomId}</p>
                            <p className="mt-1 text-xs text-slate-500">Attendees {(session.attendees || []).length}</p>
                          </div>
                          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusClass}`}>
                            {session.status.toUpperCase()}
                          </span>
                        </div>

                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => joinOnlineClass(session)}
                            disabled={joiningSessionId === session.id || !session.joinable}
                            className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold text-white shadow transition ${!session.joinable ? "cursor-not-allowed bg-slate-400" : "bg-sky-600 hover:bg-sky-700"}`}
                          >
                            <FaExternalLinkAlt />
                            {joiningSessionId === session.id ? "Joining..." : alreadyJoined ? "Rejoin class" : "Join class"}
                          </button>
                          {alreadyJoined && (
                            <span className="rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                              Attendance marked
                            </span>
                          )}
                        </div>

                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {(!isViewing && activeSection === "attendance" && attendanceTab === "assessments" && prefs.showAssessments) && (
              <div className="rounded-3xl border border-indigo-100 bg-[linear-gradient(170deg,#ffffff_0%,#eef2ff_55%,#faf5ff_100%)] p-6 shadow-[0_24px_60px_rgba(79,70,229,0.14)] view-fade">
                <div className="mb-4 flex items-center gap-2">
                  <FaClipboardList className="text-indigo-600" />
                  <h2 className="text-lg font-semibold tracking-wide text-slate-900">My Assessments</h2>
                </div>
                <ProctoredAssessment
                  mode="student"
                  creatorRole="student"
                  creatorName={displayName}
                  studentName={displayName}
                />
              </div>
            )}

            {(!isViewing && activeSection === "admission") && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm view-fade sm:p-5">
                <AdmissionForm embedded />
              </div>
            )}

            {isAdmissionView && (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm view-fade">
                <div className="flex items-center gap-2">
                  <FaFileAlt className="text-indigo-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Admission Form</h2>
                </div>
                <p className="mt-2 text-sm text-slate-600">
                  {activeView === "admission_fill"
                    ? "Complete your admission form with accurate details."
                    : "Review or download your submitted admission form."}
                </p>
                <div className="mt-4">
                  {activeView === "admission_fill" ? (
                    <Link
                      to="/admission-form"
                      className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 transition"
                    >
                      <FaFileAlt />
                      Open Admission Form
                    </Link>
                  ) : (
                    <Link
                      to="/view-admission-form"
                      className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition"
                    >
                      <FaFileAlt />
                      View Admission Form
                    </Link>
                  )}
                </div>
              </div>
            )}

            {(!isViewing && activeSection === "academics" && academicsTab === "assignments" && showAssignments) && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <FaClipboardList className="text-amber-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Assignments Due</h2>
                </div>
                <div className="mt-4 space-y-3">
                  {assignments.map((item) => (
                    <div key={item.title} className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-800">{item.title}</p>
                        <p className="text-sm text-slate-500">{item.subject} • Due {item.due}</p>
                      </div>
                      <span className="text-xs uppercase tracking-wide text-slate-600">{item.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(!isViewing && activeSection === "academics" && academicsTab === "exams" && showExams) && (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <FaCalendarAlt className="text-indigo-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Upcoming Exams</h2>
                </div>
                <div className="mt-4 space-y-3 md:hidden">
                  {upcomingExams.map((exam) => (
                    <div key={`${exam.date}-${exam.subject}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">{exam.date}</p>
                          <p className="mt-2 text-base font-semibold text-slate-900">{exam.subject}</p>
                        </div>
                        <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600 shadow-sm">
                          {exam.time}
                        </span>
                      </div>
                      <div className="mt-4 rounded-lg bg-white px-3 py-2 text-sm">
                        <p className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Exam Room</p>
                        <p className="mt-1 font-medium text-slate-700">{exam.room}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 hidden overflow-x-auto md:block">
                  <table className="w-full text-left text-sm">
                    <thead className="text-slate-500">
                      <tr>
                        <th className="pb-2 font-medium">Date</th>
                        <th className="pb-2 font-medium">Subject</th>
                        <th className="pb-2 font-medium">Time</th>
                        <th className="pb-2 font-medium">Room</th>
                      </tr>
                    </thead>
                    <tbody>
                      {upcomingExams.map((exam) => (
                        <tr key={`${exam.date}-${exam.subject}`} className="border-t border-slate-100">
                          <td className="py-3 text-slate-600">{exam.date}</td>
                          <td className="py-3 font-medium text-slate-700">{exam.subject}</td>
                          <td className="py-3 text-slate-600">{exam.time}</td>
                          <td className="py-3 text-slate-600">{exam.room}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {(!isViewing && activeSection === "academics" && academicsTab === "grades" && showGrades) && (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <FaGraduationCap className="text-emerald-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Recent Grades</h2>
                </div>
                <div className="mt-4 space-y-3">
                  {recentGrades.map((item) => (
                    <div key={item.assessment} className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-800">{item.assessment}</p>
                        <p className="text-sm text-slate-500">{item.subject}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-slate-700">{item.score}</p>
                        <p className="text-xs text-slate-500">{item.grade}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(!isViewing && activeSection === "academics" && academicsTab === "notices" && showNotices) && (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <FaBell className="text-rose-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Notices and Announcements</h2>
                </div>
                <div className="mt-4 space-y-4">
                  {notices.map((notice) => (
                    <div key={notice.title} className="border-l-4 border-indigo-500 pl-4">
                      <div className="flex items-center gap-2">
                        {notice.type === "Exam" ? (
                          <FaExclamationTriangle className="text-amber-600" />
                        ) : (
                          <FaCheckCircle className="text-emerald-600" />
                        )}
                        <p className="font-medium text-slate-800">{notice.title}</p>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">{notice.detail}</p>
                      <p className="text-xs text-slate-500 mt-1">{notice.date}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          )}

          {showRightColumn && (
          <div className={`space-y-6 ${isCenteredSectionLayout ? "mx-auto w-full max-w-4xl" : ""}`}>
            {((isViewing && activeView === "profile") || (!isViewing && activeSection === "account" && accountTab === "profile")) && showProfile && (
            <div id="profile" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm view-fade">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
                <div className="flex items-start gap-4">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-slate-100 text-lg font-semibold text-slate-500">
                    {studentInitials || "SP"}
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Student Account</p>
                    <h2 className="mt-2 text-2xl font-semibold text-slate-900">{studentInfo.name}</h2>
                    <p className="mt-1 text-sm text-slate-500 break-all">{studentInfo.email}</p>
                    <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
                      Your key identity details, class record, school information, and fee status in one place.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <Link
                    to="/profile"
                    className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
                  >
                    <FaUserCircle />
                    Open full profile
                  </Link>
                  {prefs.showIdCard && (
                    <button
                      type="button"
                      onClick={() => handleView("id_card")}
                      className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      <FaFileAlt />
                      View ID card
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">School</p>
                  <p className="mt-2 text-lg font-semibold text-slate-900">{schoolName}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Class</p>
                  <p className="mt-2 text-lg font-semibold text-slate-900">{studentInfo.class}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Roll Number</p>
                  <p className="mt-2 text-lg font-semibold text-slate-900">{studentInfo.rollNo}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Admission Date</p>
                  <p className="mt-2 text-lg font-semibold text-slate-900">{studentInfo.admissionDate}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Fee Status</p>
                  <p className="mt-2 text-lg font-semibold text-slate-900">{paymentStatusLabel}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Profile Status</p>
                  <p className="mt-2 text-lg font-semibold text-slate-900">
                    {studentProfileLoading ? "Refreshing" : "Active"}
                  </p>
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
                Need to change password or review full academic details? Open the full profile page for the dedicated student profile experience.
              </div>
            </div>
            )}

            {((isViewing && activeView === "attendance") || (!isViewing && activeSection === "attendance" && attendanceTab === "breakdown")) && showAttendanceBreakdown && (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm view-fade">
                <div className="flex items-center gap-2">
                  <FaCalendarCheck className="text-emerald-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Attendance Breakdown</h2>
                </div>
                <div className="mt-4 space-y-3 text-sm">
                  {attendanceBreakdown.map((item) => {
                    const percent = Math.round((item.value / item.total) * 100);
                    return (
                      <div key={item.label}>
                        <div className="flex items-center justify-between">
                          <span className={`font-medium ${item.text}`}>{item.label}</span>
                          <span className="text-slate-500">{item.value}/{item.total}</span>
                        </div>
                        <div className="mt-2 h-2 rounded-full bg-slate-200">
                          <div className={`h-2 rounded-full ${item.color}`} style={{ width: `${percent}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {((isViewing && activeView === "fees") || (!isViewing && activeSection === "account" && accountTab === "fees")) && showFees && (
            <div id="fees" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm view-fade">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <FaRegMoneyBillAlt className="text-emerald-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Fee Summary</h2>
                </div>
                <button
                  type="button"
                  onClick={() => fakeExport("Fees", "fees")}
                  className={exportButtonClass("fees")}
                  disabled={Boolean(exportingKey)}
                >
                  <FaDownload />
                  Export
                </button>
              </div>
              <div className="mt-4 space-y-2 text-sm text-slate-700">
                <p><span className="font-medium">School:</span> {schoolName}</p>
                <p><span className="font-medium">Total Paid:</span> {feeSummary.total}</p>
                <p><span className="font-medium">Payment Status:</span> {feeSummary.due}</p>
                <p><span className="font-medium">Next Due:</span> {feeSummary.nextDue}</p>
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>Payment progress</span>
                    <span>{paidPercent}%</span>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-slate-200">
                    <div className="h-2 rounded-full bg-emerald-500" style={{ width: `${paidPercent}%` }} />
                  </div>
                </div>
              </div>
            </div>
            )}

            {((isViewing && activeView === "marks") || (!isViewing && activeSection === "account" && accountTab === "marks")) && showMarks && (
            <div id="marks" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm view-fade">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <FaGraduationCap className="text-indigo-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Marks Card and CGPA</h2>
                </div>
                <button
                  type="button"
                  onClick={() => fakeExport("Marks and CGPA", "marks")}
                  className={exportButtonClass("marks")}
                  disabled={Boolean(exportingKey)}
                >
                  <FaDownload />
                  Export
                </button>
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="text-slate-500">
                    <tr>
                      <th className="pb-2 font-medium">Subject</th>
                      <th className="pb-2 font-medium">Score</th>
                      <th className="pb-2 font-medium">Grade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {marksCard.map((row) => (
                      <tr key={row.subject} className="border-t border-slate-100">
                        <td className="py-2 font-medium text-slate-700">{row.subject}</td>
                        <td className="py-2 text-slate-600">{row.score}/{row.max}</td>
                        <td className="py-2 text-slate-600">{row.grade}</td>
                      </tr>
                    ))}
                    <tr className="border-t border-slate-200 text-slate-700">
                      <td className="py-2 font-medium">Total</td>
                      <td className="py-2">{totalScore}/{totalMax}</td>
                      <td className="py-2">-</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
                <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <p className="text-xs uppercase tracking-wide text-slate-500">CGPA</p>
                  <p className="text-lg font-semibold text-slate-800">{cgpaSummary.cgpa}</p>
                </div>
                <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Percentile</p>
                  <p className="text-lg font-semibold text-slate-800">{cgpaSummary.percentile}</p>
                </div>
                <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Rank</p>
                  <p className="text-lg font-semibold text-slate-800">{cgpaSummary.rank}</p>
                </div>
              </div>
            </div>
            )}

            {((isViewing && activeView === "id_card") || (!isViewing && activeSection === "account" && accountTab === "id_card")) && showIdCard && (
            <div id="id-card" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm view-fade">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">School ID</p>
                  <h2 className="text-lg font-semibold text-slate-900">Student ID Card</h2>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => fakeExport("ID Card", "id-card")}
                    className={exportButtonClass("id-card")}
                    disabled={Boolean(exportingKey)}
                  >
                    <FaDownload />
                    Download
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowIdPreview(true)}
                    className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 transition"
                  >
                    <FaUserCircle />
                    Preview
                  </button>
                  <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                    Active
                  </span>
                </div>
              </div>
              <div className="mt-5 grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(260px,0.8fr)]">
                <div className="overflow-hidden rounded-[28px] bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 p-[1px] shadow-lg">
                  <div className="relative rounded-[27px] bg-[radial-gradient(circle_at_top_right,_rgba(96,165,250,0.35),_transparent_30%),linear-gradient(135deg,_#0f172a_0%,_#111827_52%,_#1e1b4b_100%)] p-6 text-white">
                    <div className="absolute -right-12 -top-12 h-36 w-36 rounded-full bg-cyan-400/20 blur-3xl" />
                    <div className="absolute -left-10 bottom-0 h-28 w-28 rounded-full bg-amber-300/20 blur-3xl" />
                    <div className="relative flex items-start justify-between gap-4">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-white/65">{idCardInfo.schoolName}</p>
                        <h3 className="mt-3 text-2xl font-semibold">Student Identity Card</h3>
                      </div>
                      <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-200">
                        Active
                      </span>
                    </div>

                    <div className="relative mt-8 flex flex-col gap-5 sm:flex-row sm:items-center">
                      <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-[24px] border border-white/15 bg-white/10 text-3xl font-semibold backdrop-blur-sm">
                        {studentInitials || "SP"}
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.22em] text-white/55">Card Holder</p>
                        <p className="mt-2 text-2xl font-semibold">{idCardInfo.studentName}</p>
                        <p className="mt-1 text-sm text-white/70">{idCardInfo.class} • Roll {idCardInfo.rollNo}</p>
                        <p className="mt-1 text-sm text-white/70">Admission No. {idCardInfo.admissionNo}</p>
                      </div>
                    </div>

                    <div className="relative mt-8 grid grid-cols-2 gap-3 text-sm">
                      <div className="rounded-2xl border border-white/12 bg-white/10 p-3 backdrop-blur-sm">
                        <p className="text-[10px] uppercase tracking-[0.18em] text-white/55">Blood Group</p>
                        <p className="mt-2 font-semibold">{idCardInfo.bloodGroup}</p>
                      </div>
                      <div className="rounded-2xl border border-white/12 bg-white/10 p-3 backdrop-blur-sm">
                        <p className="text-[10px] uppercase tracking-[0.18em] text-white/55">Valid Thru</p>
                        <p className="mt-2 font-semibold">{idCardInfo.validThru}</p>
                      </div>
                    </div>

                    <div className="relative mt-8 flex items-end justify-between gap-4 border-t border-white/10 pt-5">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.18em] text-white/50">Support Contact</p>
                        <p className="mt-2 text-sm font-medium text-white/85">{idCardInfo.emergencyContact}</p>
                      </div>
                      <div className="grid grid-cols-4 gap-1 rounded-xl bg-white/10 p-2">
                        {Array.from({ length: 24 }).map((_, index) => (
                          <span
                            key={`mini-qr-${index}`}
                            className={`h-2.5 w-2.5 rounded-[2px] ${index % 3 === 0 || index % 5 === 0 ? "bg-white/90" : "bg-white/20"}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Verification Details</p>
                  <div className="mt-4 space-y-3 text-sm">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">School</p>
                      <p className="mt-2 font-semibold text-slate-900">{idCardInfo.schoolName}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Guardian</p>
                      <p className="mt-2 font-semibold text-slate-900">{idCardInfo.guardianName}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Issued On</p>
                      <p className="mt-2 font-semibold text-slate-900">{studentInfo.admissionDate}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Emergency Contact</p>
                      <p className="mt-2 font-semibold text-slate-900">{idCardInfo.emergencyContact}</p>
                    </div>
                  </div>
                  <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-600">
                    This live card reflects the current student data shown across the dashboard and profile.
                  </div>
                </div>
              </div>
            </div>
            )}

            {((isViewing && activeView === "leave") || (!isViewing && activeSection === "resources" && resourcesTab === "leave")) && showLeave && (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm view-fade">
                <div className="flex items-center gap-2">
                  <FaCalendarCheck className="text-emerald-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Apply for Leave</h2>
                </div>
                <form onSubmit={handleLeaveSubmit} className="mt-4 space-y-3 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="date"
                      value={leaveForm.from}
                      onChange={(e) => setLeaveForm((prev) => ({ ...prev, from: e.target.value }))}
                      className="w-full rounded-md border border-slate-200 px-3 py-2"
                      min={new Date().toISOString().split("T")[0]}
                    />
                    <input
                      type="date"
                      value={leaveForm.to}
                      onChange={(e) => setLeaveForm((prev) => ({ ...prev, to: e.target.value }))}
                      className="w-full rounded-md border border-slate-200 px-3 py-2"
                      min={leaveForm.from || new Date().toISOString().split("T")[0]}
                    />
                  </div>
                  <textarea
                    rows="3"
                    value={leaveForm.reason}
                    onChange={(e) => setLeaveForm((prev) => ({ ...prev, reason: e.target.value }))}
                    className="w-full rounded-md border border-slate-200 px-3 py-2"
                    placeholder="Reason for leave"
                  />
                  <button type="submit" className="w-full rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 transition">
                    Submit Leave Request
                  </button>
                </form>
                <div className="mt-5 border-t border-slate-100 pt-4">
                  <h3 className="text-sm font-semibold text-slate-800">Leave History</h3>
                  <div className="mt-3 space-y-2 text-sm">
                    {leaveHistory.map((item) => (
                      <div key={item.id} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2">
                        <div>
                          <p className="font-medium text-slate-800">{item.from} to {item.to}</p>
                          <p className="text-xs text-slate-500">{item.reason}</p>
                        </div>
                        <span className={`rounded-full px-3 py-1 text-xs font-medium ${leaveStatusClass(item.status)}`}>
                          {item.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {(!isViewing && activeSection === "resources" && resourcesTab === "ask" && showAskTeacher) && (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <FaRegStickyNote className="text-amber-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Ask Your Teacher</h2>
                </div>
                <form onSubmit={handleQuerySubmit} className="mt-4 space-y-3 text-sm">
                  <input
                    value={queryForm.subject}
                    onChange={(e) => setQueryForm((prev) => ({ ...prev, subject: e.target.value }))}
                    className="w-full rounded-md border border-slate-200 px-3 py-2"
                    placeholder="Subject"
                  />
                  <textarea
                    rows="3"
                    value={queryForm.message}
                    onChange={(e) => setQueryForm((prev) => ({ ...prev, message: e.target.value }))}
                    className="w-full rounded-md border border-slate-200 px-3 py-2"
                    placeholder="Write your question"
                  />
                  <button type="submit" className="w-full rounded-md bg-amber-500 px-3 py-2 text-sm font-medium text-white hover:bg-amber-600 transition">
                    Send Query
                  </button>
                </form>
              </div>
            )}

            {(!isViewing && activeSection === "resources" && resourcesTab === "classmates" && showClassmates) && (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <FaUsers className="text-indigo-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Classmates</h2>
                </div>
                {classmatesLoading ? (
                  <div className="flex justify-center py-6"><span className="animate-spin text-indigo-500">⏳</span></div>
                ) : classmatesList.length === 0 ? (
                  <p className="mt-3 text-sm text-slate-500">No classmates found.</p>
                ) : (
                  <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-slate-700">
                    {classmatesList.map((cm) => (
                      <button
                        key={cm.id || cm.name}
                        type="button"
                        onClick={() => {
                          setSelectedClassmate(cm);
                          setClassmateProfile(null);
                          setClassmateProfileLoading(true);
                          api
                            .get(API_PATHS.student.classmateProfile(cm.id))
                            .then((res) => setClassmateProfile(res.data?.data ?? res.data ?? cm))
                            .catch(() => setClassmateProfile(cm))
                            .finally(() => setClassmateProfileLoading(false));
                        }}
                        className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-left hover:bg-indigo-50 hover:border-indigo-200 transition-colors"
                      >
                        <p className="font-medium">{cm.name}</p>
                        {cm.rollNo && <p className="text-xs text-slate-400">Roll: {cm.rollNo}</p>}
                      </button>
                    ))}
                  </div>
                )}

                {/* Classmate profile modal */}
                {selectedClassmate && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                    <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-bold text-slate-900">Student Profile</h3>
                        <button type="button" onClick={() => { setSelectedClassmate(null); setClassmateProfile(null); }} className="rounded-full p-1 text-slate-400 hover:bg-slate-100">
                          ✕
                        </button>
                      </div>
                      {classmateProfileLoading ? (
                        <p className="mt-4 text-sm text-slate-400">Loading profile…</p>
                      ) : (
                        <div className="mt-4 space-y-2 text-sm text-slate-700">
                          <p><span className="font-semibold">Name:</span> {classmateProfile?.name || selectedClassmate.name}</p>
                          {(classmateProfile?.rollNo || selectedClassmate.rollNo) && <p><span className="font-semibold">Roll No:</span> {classmateProfile?.rollNo || selectedClassmate.rollNo}</p>}
                          {(classmateProfile?.className || selectedClassmate.className) && <p><span className="font-semibold">Class:</span> {classmateProfile?.className || selectedClassmate.className}</p>}
                          {(classmateProfile?.section || selectedClassmate.section) && <p><span className="font-semibold">Section:</span> {classmateProfile?.section || selectedClassmate.section}</p>}
                          {classmateProfile?.email && <p><span className="font-semibold">Email:</span> {classmateProfile.email}</p>}
                          {classmateProfile?.parentName && <p><span className="font-semibold">Parent:</span> {classmateProfile.parentName}</p>}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {(!isViewing && activeSection === "resources" && resourcesTab === "notes" && showNotes) && (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <FaRegStickyNote className="text-amber-600" />
                    <h2 className="text-lg font-semibold text-slate-900">Teacher Notes and Resources</h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => fakeExport("Notes and Resources", "notes")}
                    className={exportButtonClass("notes")}
                    disabled={Boolean(exportingKey)}
                  >
                    <FaDownload />
                    Export
                  </button>
                </div>
                <div className="mt-4 space-y-3 text-sm">
                  {teacherNotes.map((note) => (
                    <div key={note.title} className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-800">{note.title}</p>
                        <p className="text-xs text-slate-500">{note.subject} • {note.type} • {note.date}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => fakeExport(note.title, `note-${note.title}`)}
                        className={exportButtonClass(`note-${note.title}`)}
                        disabled={Boolean(exportingKey)}
                      >
                        <FaDownload />
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(!isViewing && activeSection === "resources" && resourcesTab === "library" && showLibrary) && (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <FaBook className="text-indigo-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Library and Resources</h2>
                </div>
                <div className="mt-4 space-y-3 text-sm">
                  {libraryLoans.map((item) => (
                    <div key={item.title} className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-800">{item.title}</p>
                        <p className="text-xs text-slate-500">Due {item.due}</p>
                      </div>
                      <span className="text-xs uppercase tracking-wide text-slate-600">{item.status}</span>
                    </div>
                  ))}
                  <div className="rounded-lg border border-dashed border-slate-200 px-4 py-3 text-slate-500">
                    Digital resources: 4 new worksheets available this week.
                  </div>
                </div>
              </div>
            )}

            {(!isViewing && activeSection === "resources" && resourcesTab === "links" && showQuickLinks) && (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900">Quick Links</h2>
                <div className="mt-3 space-y-2 text-sm">
                  <Link to="/student/usage" className="block rounded-md border border-slate-200 px-3 py-2 text-slate-700 hover:bg-slate-50">
                    Time Spent on Platform
                  </Link>
                  <Link to="/profile" className="block rounded-md border border-slate-200 px-3 py-2 text-slate-700 hover:bg-slate-50">
                    View Profile
                  </Link>
                  <Link to="/admission-form" className="block rounded-md border border-slate-200 px-3 py-2 text-slate-700 hover:bg-slate-50">
                    Update Admission Form
                  </Link>
                  <Link to="/view-admission-form" className="block rounded-md border border-slate-200 px-3 py-2 text-slate-700 hover:bg-slate-50">
                    Download Admission Copy
                  </Link>
                </div>
              </div>
            )}
          </div>
          )}
        </section>
        )}

          </div>
        </section>
      </div>

      {showCustomize && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-3 py-4 backdrop-blur-sm sm:px-4">
          <div className={`flex w-full max-w-xl max-h-[82vh] flex-col overflow-hidden rounded-3xl border p-4 shadow-2xl sm:p-5 ${isDarkMode ? "border-slate-800 bg-slate-900 text-slate-100" : "border-slate-200 bg-white text-slate-900"}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Customize</p>
                <h2 className="mt-1 text-xl font-bold">Personalize your dashboard</h2>
              </div>
              <button
                type="button"
                onClick={() => setShowCustomize(false)}
                className={`rounded-full px-3 py-1 text-sm font-semibold transition ${isDarkMode ? "bg-slate-800 text-slate-200 hover:bg-slate-700" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
              >
                Close
              </button>
            </div>
            <div className="mt-4 flex-1 overflow-y-auto pr-1">
              <div className="rounded-2xl border border-slate-200 p-3 sm:p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Appearance</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setDraftTheme("light")}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition sm:px-4 sm:py-2 sm:text-sm ${draftTheme === "light"
                      ? "bg-amber-500 text-white"
                      : isDarkMode
                        ? "border border-slate-700 text-slate-200 hover:bg-slate-800"
                        : "border border-slate-300 text-slate-700 hover:bg-slate-100"
                    }`}
                  >
                    Light mode
                  </button>
                  <button
                    type="button"
                    onClick={() => setDraftTheme("dark")}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition sm:px-4 sm:py-2 sm:text-sm ${draftTheme === "dark"
                      ? "bg-slate-900 text-white"
                      : isDarkMode
                        ? "border border-slate-700 text-slate-200 hover:bg-slate-800"
                        : "border border-slate-300 text-slate-700 hover:bg-slate-100"
                    }`}
                  >
                    Dark mode
                  </button>
                </div>
            </div>
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {[...attendancePreferenceOptions, ...preferenceOptions].map((option) => (
                  <button
                    type="button"
                    key={option.key}
                    onClick={() => togglePref(option.key)}
                    className={`flex items-center justify-between rounded-xl border px-3 py-2 text-left text-sm transition ${draftPrefs[option.key]
                      ? isDarkMode
                        ? "border-amber-500/30 bg-amber-500/10 text-amber-200"
                        : "border-amber-200 bg-amber-50 text-amber-700"
                      : isDarkMode
                        ? "border-slate-800 bg-slate-950 text-slate-300"
                        : "border-slate-200 bg-slate-50 text-slate-600"
                    }`}
                  >
                    <span className="font-semibold">{option.label}</span>
                    <span className={`inline-flex h-5 w-9 items-center rounded-full p-1 transition ${draftPrefs[option.key] ? "bg-amber-500" : isDarkMode ? "bg-slate-800" : "bg-slate-300"}`}>
                      <span className={`h-3.5 w-3.5 rounded-full bg-white transition ${draftPrefs[option.key] ? "translate-x-4" : "translate-x-0"}`} />
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-200 pt-3">
              <button
                type="button"
                onClick={applyCustomization}
                disabled={!canApply}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${canApply ? "bg-amber-500 text-white hover:bg-amber-600" : "cursor-not-allowed bg-slate-300 text-slate-500"}`}
              >
                Apply changes
              </button>
              <button
                type="button"
                onClick={resetPrefs}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${isDarkMode ? "border border-slate-700 text-slate-200 hover:bg-slate-800" : "border border-slate-300 text-slate-700 hover:bg-slate-100"}`}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentDashboard;









